/**
 * mcpFormat
 * =========
 * Turns an MCP (Model Context Protocol) tools/call result into a STRUCTURED
 * description that the SapResult component renders as real UI (tables, key/value
 * grids, JSON trees, lists, prose) — WITHOUT any LLM.
 *
 * MCP results are usually shaped like:
 *   { content: [ { type: 'text', text: '...' }, ... ] }
 * where each `text` is frequently a JSON string (an array of records, an OData
 * envelope { value: [...] }, or an object). SAP's MCP server returns payloads
 * this way. Rather than flatten everything to Markdown (which overflows and
 * loses nesting), we classify the data so the UI can present each shape well.
 *
 * parseMcpResult(result) -> an array of "blocks", each one of:
 *   { kind: 'table',    columns: string[], rows: object[], count: number }
 *   { kind: 'keyvalue', pairs: [key, value][] }
 *   { kind: 'list',     items: (string|number|boolean)[] }
 *   { kind: 'json',     value: any }            // nested / irregular -> tree
 *   { kind: 'markdown', text: string }          // looks like Markdown
 *   { kind: 'text',     text: string }          // plain prose
 *   { kind: 'empty' }                           // nothing to show
 *
 * Everything here is pure (no React) so it is easy to reason about and test.
 */

const isScalar = (v) =>
  v === null ||
  v === undefined ||
  typeof v === 'string' ||
  typeof v === 'number' ||
  typeof v === 'boolean';

// A non-empty array of plain objects whose values are all scalar => table rows.
const isArrayOfFlatObjects = (arr) =>
  Array.isArray(arr) &&
  arr.length > 0 &&
  arr.every(
    (row) => row && typeof row === 'object' && !Array.isArray(row) && Object.values(row).every(isScalar)
  );

// Columns = union of keys across rows, preserving first-seen order.
const unionColumns = (arr) => {
  const cols = [];
  arr.forEach((row) => {
    Object.keys(row).forEach((k) => {
      if (!cols.includes(k)) cols.push(k);
    });
  });
  return cols;
};

// Pull a record array out of a common envelope shape.
const envelopeArray = (obj) => {
  if (Array.isArray(obj.value)) return obj.value;
  if (Array.isArray(obj.results)) return obj.results;
  if (Array.isArray(obj.items)) return obj.items;
  if (Array.isArray(obj.data)) return obj.data;
  if (Array.isArray(obj.records)) return obj.records;
  return null;
};

// Heuristic: does this string look like Markdown worth rendering as such?
const looksLikeMarkdown = (s) => {
  const t = s || '';
  return (
    /(^|\n)\s*#{1,6}\s+/.test(t) || // headings
    /(^|\n)\s*[-*+]\s+/.test(t) || // bullet list
    /(^|\n)\s*\d+\.\s+/.test(t) || // ordered list
    /```/.test(t) || // code fence
    /\|.*\|/.test(t) || // pipe table
    /\*\*[^*]+\*\*/.test(t) // bold
  );
};

// Classify a single already-parsed JSON value into one block.
const classifyJson = (value) => {
  if (isScalar(value)) {
    const s = value === null || value === undefined ? '' : String(value);
    return s.trim() ? { kind: 'text', text: s } : { kind: 'empty' };
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return { kind: 'empty' };
    if (isArrayOfFlatObjects(value)) {
      return { kind: 'table', columns: unionColumns(value), rows: value, count: value.length };
    }
    if (value.every(isScalar)) {
      return { kind: 'list', items: value };
    }
    return { kind: 'json', value };
  }

  // Object: surface an envelope record array as a table when present.
  const recs = envelopeArray(value);
  if (recs && isArrayOfFlatObjects(recs)) {
    return { kind: 'table', columns: unionColumns(recs), rows: recs, count: recs.length };
  }
  if (recs && recs.length === 0) return { kind: 'empty' };

  // Flat object -> key/value grid; nested object -> JSON tree.
  if (Object.values(value).every(isScalar)) {
    return { kind: 'keyvalue', pairs: Object.entries(value) };
  }
  return { kind: 'json', value };
};

const tryParseJson = (s) => {
  const t = (s || '').trim();
  if (!t) return { ok: false };
  if (!(t.startsWith('{') || t.startsWith('['))) return { ok: false };
  try {
    return { ok: true, value: JSON.parse(t) };
  } catch (e) {
    return { ok: false };
  }
};

// One MCP text part -> one block (JSON structured, Markdown, or plain text).
const classifyTextPart = (text) => {
  const parsed = tryParseJson(text);
  if (parsed.ok) return classifyJson(parsed.value);
  if (looksLikeMarkdown(text)) return { kind: 'markdown', text };
  return text && text.trim() ? { kind: 'text', text } : { kind: 'empty' };
};

/**
 * parseMcpResult(result) -> block[]
 * `result` is the JSON-RPC `result` object from a tools/call response.
 */
export const parseMcpResult = (result) => {
  if (result == null) return [{ kind: 'empty' }];
  if (typeof result === 'string') return [classifyTextPart(result)];

  const blocks = [];
  const content = result.content;
  if (Array.isArray(content)) {
    content.forEach((c) => {
      if (!c || typeof c !== 'object') return;
      if (typeof c.text === 'string') blocks.push(classifyTextPart(c.text));
      else if (c.json != null) blocks.push(classifyJson(c.json));
    });
  }

  if (result.structuredContent != null) blocks.push(classifyJson(result.structuredContent));

  const meaningful = blocks.filter((b) => b.kind !== 'empty');
  if (meaningful.length) return meaningful;

  // Fallback: classify the whole result object.
  const whole = classifyJson(result);
  return [whole];
};

// Flatten the parsed blocks into a plain-text representation (used for the
// per-message "Copy" button). Tables become TSV, everything else JSON/text.
export const blocksToPlainText = (blocks) =>
  (blocks || [])
    .map((b) => {
      if (b.kind === 'table') {
        const head = b.columns.join('\t');
        const body = b.rows
          .map((r) => b.columns.map((c) => (r[c] == null ? '' : String(r[c]))).join('\t'))
          .join('\n');
        return `${head}\n${body}`;
      }
      if (b.kind === 'keyvalue') return b.pairs.map(([k, v]) => `${k}: ${v}`).join('\n');
      if (b.kind === 'list') return b.items.map((i) => `- ${i}`).join('\n');
      if (b.kind === 'json') return JSON.stringify(b.value, null, 2);
      if (b.kind === 'markdown' || b.kind === 'text') return b.text;
      return '';
    })
    .filter(Boolean)
    .join('\n\n');

// Build a CSV string from a table block (for the Download CSV action).
export const tableToCsv = (block) => {
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = block.columns.map(esc).join(',');
  const body = block.rows.map((r) => block.columns.map((c) => esc(r[c])).join(',')).join('\n');
  return `${head}\n${body}`;
};

export const __test = { classifyJson, isArrayOfFlatObjects, unionColumns, envelopeArray };

export default parseMcpResult;
